<template>
  <ListLayout
    :title="`${itemModule.itemPlural}`"
    :context="context"
    :tableData="itemList"
    :tableHeader="tableHeaders"
    :total="totalItems"
    @getList="getItemList"
    ref="listTable"
  >
    <template #listAction>
      <SecondaryButton
        v-if="permissions && permissions.view"
        id="itemsExport"
        btnClass="mr-2 rounded-lg"
        outlined
        iconClass="secondary--text mr-2"
        name="Export"
        icon="mdi-download"
        iconSize="lg"
        @click="openDownloadDialog = true"
      />
      <v-menu offset-y>
        <template v-slot:activator="{ on: menu, attrs }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on: tooltip }">
              <div v-on="{ ...tooltip }">
                <PrimaryButton
                  id="itemsAdd"
                  v-if="permissions && permissions.add"
                  :disabled="isButtonDisabled"
                  v-bind="attrs"
                  v-on="{ ...menu }"
                  :name="`Add ${itemModule.itemPlural}`"
                  btnClass="rounded-lg"
                  icon="mdi-plus"
                  iconSize="x-large"
                >
                </PrimaryButton>
              </div>
            </template>
            <span>{{ buttonTooltip }}</span>
          </v-tooltip>
        </template>
        <v-list dense flat>
          <v-list-item id="add-item" @click="addItem">
            <v-list-item-title>Add {{ itemModule.item }}</v-list-item-title>
          </v-list-item>
          <v-list-item id="import-item" @click="showBulkUploadDialog = true">
            <v-list-item-title>Import {{ itemModule.item }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
    <template #rightFilterSlot>
      <div class="d-flex align-center justify-end w-100">
        <RefreshListButton
          id="itemsRefreshList"
          :isIcon="false"
          @click="$refs.listTable.searchTriggered()"
        />

        <ItemFilter
          :filter="itemFilters"
          @apply="
            $refs.listTable.itemsPerPageChanged($globalConstant.itemsPerPage)
          "
          @reset="resetFilter"
          :isChips="isChipsVisible"
        />
      </div>
    </template>
    <template #dialogs>
      <ItemForm
        :editMode="isEditing"
        :itemID="selectedItemID"
        v-model="showItemForm"
        @refreshList="
          checkSubscriptionLimit();
          $refs.listTable.itemsPerPageChanged($globalConstant.itemsPerPage);
        "
      />
      <DownloadSheet
        v-model="openDownloadDialog"
        title="Download Item Sheet"
        module="item"
        method="downloadItemSheet"
      />
      <BulkUpload
        uploadTo="item"
        v-model="showBulkUploadDialog"
        :hasBulkActions="true"
        @refreshList="
          checkSubscriptionLimit();
          getItemList({ limit: 10, offset: 0 });
        "
      />
    </template>
  </ListLayout>
</template>

<script>
/* eslint-disable vue/no-unused-components */
import { bus } from "@/main.js";
import ItemButton from "@/components/Button/ItemButton.vue";
import ListLayout from "@/components/LayoutComponents/ListLayout.vue";
import ItemForm from "@/components/ModuleBasedComponents/Item/ItemForm.vue";
import DownloadSheet from "@/components/GeneralComponents/DownloadSheet.vue";
import ItemFilter from "@/components/ModuleBasedComponents/Item/ItemFilter.vue";
import BulkUpload from "@/components/BaseComponents/BulkUpload.vue";
import ItemStorageTypeChip from "@/components/ModuleBasedComponents/Item/ItemStorageTypeChip.vue";
import RefreshListButton from "@/components/BaseComponents/RefreshListButton.vue";
import { mapState } from "vuex";
import { filterModules } from "@/utils/fieldConstants";
import { itemModule } from "@/utils/moduleMapping";

export default {
  components: {
    ItemButton,
    ListLayout,
    ItemForm,
    DownloadSheet,
    ItemFilter,
    BulkUpload,
    ItemStorageTypeChip,
    RefreshListButton,
  },
  data() {
    return {
      itemModule,
      permissions: null,
      sorting: {},
      openDownloadDialog: false,
      itemsPerPage: 10,
      pageNo: 1,
      totalItems: 0,
      isChipsVisible: false,
      itemList: [],
      headerSelected: [
        {
          headerName: "Item Number",
          field: "item_no",
          sortable: true,
          pinned: "left",
          minWidth: 200,
        },
        {
          headerName: "Storage Type",
          field: "storage_type",
          sortable: false,
          cellRenderer: "ItemStorageTypeChip",
        },
        {
          headerName: "Case Factor",
          field: "case_factor",
          sortable: false,
        },
        {
          headerName: "Length",
          field: "length",
          sortable: false,
        },
        {
          headerName: "Height",
          field: "height",
          sortable: true,
        },
        {
          headerName: "Width",
          field: "width",
          sortable: true,
        },
        {
          headerName: "Weight",
          field: "weight",
          sortable: true,
        },
        {
          headerName: "Volume",
          field: "volume",
          sortable: true,
        },
        // {
        //   headerName: "Unit",
        //   field: "unit",
        //   sortable: true,
        // },
        {
          headerName: "description",
          field: "description",
          sortable: true,
          tooltipField: "description",
        },
      ],

      isEditing: false,
      showItemForm: false,
      selectedItemID: null,

      isButtonDisabled: false,
      buttonTooltip: "Add Items",

      showBulkUploadDialog: false,
    };
  },
  computed: {
    tableHeaders() {
      return [
        ...this.headerSelected,
        {
          headerName: "Actions",
          field: "actions",
          pinned: "right",
          minWidth: 100,
          sortable: false,
          width: 100,
          cellRenderer: "ItemButton",
        },
      ];
    },
    context() {
      return { parentComponent: this };
    },
    profileObject() {
      let obj = localStorage.getItem("user_data");
      return obj ? JSON.parse(obj) : null;
    },
    ...mapState("userProfile", {
      itemFilters: (state) => state.item_filters,
    }),
  },
  methods: {
    checkSubscriptionLimit() {
      return this.$api.company
        .checkSubscriptionLimit(this.profileObject.company, {
          module: "itemmaster",
        })
        .then(() => {
          this.isButtonDisabled = false;
        })
        .catch((err) => {
          bus.$emit("showToastMessage", {
            message: err.data.non_field_errors[0],
            color: "error",
            timeout: 4000,
          });
          this.isButtonDisabled = true;
          this.buttonTooltip = err.data.non_field_errors[0];
          isItemAddedTable = false;
        });
    },
    resetFilter() {
      this.$store.commit("userProfile/resetFilters", {
        moduleKey: "item_filters",
      });
      this.getItemList({ limit: 10, offset: 0 });
    },
    async getItemList(params = {}) {
      bus.$emit("showLoader", true);
      params = {
        ...params,
        ...this.itemFilters,
      };

      // if ("ordering" in params && params.ordering.length > 0) {
      //   params = { ...params, ordering: params.ordering.join(",") };
      // }
      if ("storage_type" in params && params.storage_type.length > 0) {
        params = { ...params, storage_type: params.storage_type.join(",") };
      }
      if ("unit" in params && params.unit.length > 0) {
        params = { ...params, unit: params.unit.join(",") };
      }

      await this.$store.dispatch("userProfile/updateUserFilters", {
        filter_data: { ...this.itemFilters },
        filter_type: filterModules.ITEM,
      });

      this.isChipsVisible = Object.keys(this.itemFilters).length > 0;

      this.$api.item
        .getItemList(params)
        .then((res) => {
          this.itemList = res.data;
          this.totalItems = res.count;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.log(err);
          bus.$emit("showLoader", false);
        });
    },
    addItem() {
      this.selectedItemID = null;
      this.isEditing = false;
      this.showItemForm = true;
    },
    editItem(id) {
      this.selectedItemID = id;
      this.isEditing = true;
      this.showItemForm = true;
    },
  },
  created() {
    this.permissions = this.$permissions.getPermission("item");
    this.checkSubscriptionLimit();
  },
};
</script>
